import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Inbox, MessageSquare, Heart, Wallet, AlertTriangle, AlertCircle, Trash2, CheckCheck, Circle } from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

const TYPE_META = {
  budget_warn: { Icon: Wallet, color: "text-coral-500", bg: "bg-coral-500/10" },
  budget_limit: { Icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-500/15" },
  budget_over: { Icon: AlertCircle, color: "text-destructive", bg: "bg-destructive/10" },
  community_comment: { Icon: MessageSquare, color: "text-primary", bg: "bg-primary/10" },
  community_reaction: { Icon: Heart, color: "text-pink-600", bg: "bg-pink-500/10" }
};

export default function InboxPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try {
      const r = await apiClient.get("/notifications");
      setNotes(r.data);
    } catch { toast.error("Failed to load notifications"); }
    setLoading(false);
  };

  const markRead = async (id) => {
    try {
      await apiClient.patch(`/notifications/${id}/read`);
      setNotes(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await apiClient.post("/notifications/mark-all-read");
      setNotes(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All marked as read");
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch { toast.error("Failed"); }
  };

  const visible = filter === "unread" ? notes.filter(n => !n.read) : notes;
  const unreadCount = notes.filter(n => !n.read).length;

  if (loading) return <div className="space-y-6 animate-pulse"><div className="h-10 bg-muted rounded-lg w-48" /><div className="h-24 bg-muted rounded-2xl" /><div className="h-24 bg-muted rounded-2xl" /></div>;

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Inbox className="h-8 w-8" />
            Inbox
          </h1>
          <p className="text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}` : "All caught up!"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("all")}
            className="rounded-full"
            data-testid="filter-all"
          >All</Button>
          <Button
            variant={filter === "unread" ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter("unread")}
            className="rounded-full"
            data-testid="filter-unread"
          >Unread {unreadCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive text-[10px] font-bold">{unreadCount}</span>}</Button>
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={markAllRead} className="rounded-full" data-testid="mark-all-read-btn">
              <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      {visible.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl"><CardContent className="py-16 text-center">
          <Inbox className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">{filter === "unread" ? "Nothing unread" : "No notifications yet"}</h3>
          <p className="text-muted-foreground">You&apos;ll see budget warnings and community activity here.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {visible.map(n => {
            const meta = TYPE_META[n.type] || { Icon: Inbox, color: "text-muted-foreground", bg: "bg-muted" };
            const Icon = meta.Icon;
            return (
              <Card
                key={n.id}
                className={`border-0 shadow-md rounded-2xl transition-colors ${!n.read ? "bg-primary/5 border-l-4 border-primary" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <CardContent className="p-4 flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm">{n.title}</h3>
                      {!n.read && <Circle className="h-2 w-2 fill-primary text-primary" />}
                    </div>
                    {n.body && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      <span>{formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</span>
                      {n.link && (
                        <Link
                          to={n.link}
                          onClick={() => markRead(n.id)}
                          className="text-primary hover:underline font-medium"
                          data-testid={`open-notification-${n.id}`}
                        >
                          View →
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!n.read && (
                      <Button variant="ghost" size="icon" onClick={() => markRead(n.id)} title="Mark as read" className="h-8 w-8 rounded-full" data-testid={`mark-read-${n.id}`}>
                        <CheckCheck className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(n.id)} title="Delete" className="h-8 w-8 rounded-full text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
